import React from 'react';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import proptypes from 'prop-types';
import classNames from 'classnames';

import { InstoreOrderDetails } from '../orderDetails/inStoreOrderDetails';
import { OnlineOrderDetails } from '../orderDetails/onlineOrderDetails';
import { InstoreEditPOJobName } from '../editPOJobName/instoreEditPOJobName';
import { OnlineEditPOJobName } from '../editPOJobName/onlineEditPOJobName';
import { RecentPurchasesTileDate } from '../subcomponents/orderDate/RecentPurchasesDate';
import { RecentPurchasesTileJobName } from '../subcomponents/jobName/RecentPurchasesJobName';
import { RecentPurchasesTileOrigin } from '../subcomponents/orderOrigin/RecentPurchasesOrigin';
import { RecentPurchasesTileReceipt } from '../subcomponents/orderReceipt/RecentPurchasesReceipt';
import { RecentPurchasesTileTotal } from '../subcomponents/orderTotal/RecentPurchasesTotal';
import { OrderStatus, LazyOrderStatus } from '../subcomponents/orderStatus';
import { OrderItems, LazyOrderItems } from '../subcomponents/orderItems';
import {
  createOrderDetailsURL,
  getStoreNumber,
  isOnlineOrder,
} from '../helpers';

const {
  arrayOf, shape, string, number, func, bool
} = proptypes;

const LAZY_LOAD_INDEX_THRESHOLD = 2;

export function RecentPurchasesTile({
  customerInfo = {},
  index = undefined,
  onError = () => {},
  order = {},
  isDashboardSnapshot = false,
}) {
  const isOnline = order.orderNumbers && order.orderNumbers[0] ? isOnlineOrder(order.orderNumbers[0]) : false;
  const storeNumber = getStoreNumber(order);
  const orderDetailsUrl = createOrderDetailsURL({
    isOnline,
    order,
    customerInfo,
    storeNumber,
  });
  const tileId = isOnline ? order.orderNumbers?.[0] : order.receiptDetails;

  const { ref, clickID } = useImpression({
    data: {
      id: tileId,
      name: 'RecentPurchasesTile',
      component: 'RecentPurchasesTile',
      position: (index ?? 0) + 1,
      type: 'content',
    },
  });

  const EditPOJobName = isOnline ? OnlineEditPOJobName : InstoreEditPOJobName;
  const OrderDetailsType = isOnline ? OnlineOrderDetails : InstoreOrderDetails;
  const OrderStatusComponent = index < LAZY_LOAD_INDEX_THRESHOLD ? OrderStatus : LazyOrderStatus;
  const OrderItemsComponent = index < LAZY_LOAD_INDEX_THRESHOLD ? OrderItems : LazyOrderItems;

  return (
    <QueryProvider cacheKey={order.receiptDetails}>
      <div
        data-testid={`recentOrders-contentTile-b2b${index}`}
        className={classNames(
          'sui-w-full sui-flex sui-flex-col sui-border-1 sui-border-solid sui-border-primary sui-p-4',
          isDashboardSnapshot ? 'sui-rounded-md' : 'sui-rounded-sm'
        )}
        ref={ref}
        // eslint-disable-next-line react/no-unknown-property
        clickid={clickID}
      >
        <OrderStatusComponent
          customerInfo={customerInfo}
          isOnline={isOnline}
          layout="tile"
          order={order}
          OrderDetailsType={OrderDetailsType}
          index={index}
        />
        <div className="sui-flex sui-flex-col sui-gap-2">
          <RecentPurchasesTileReceipt
            orderReceiptDetails={
              isOnline ? order.orderNumbers[0] : order.receiptDetails
            }
            href={orderDetailsUrl}
          />
          <RecentPurchasesTileDate orderDate={order.salesDate} />
          <QueryProvider cacheKey={order.POJobName}>
            <EditPOJobName order={order} customerInfo={customerInfo}>
              {({ mutatePOJobName }) => (
                <RecentPurchasesTileJobName
                  POJobName={order.POJobName}
                  customerInfo={customerInfo}
                  order={order}
                  isOnline={isOnline}
                  onError={onError}
                  mutatePOJobName={mutatePOJobName}
                  storeNumber={storeNumber}
                />
              )}
            </EditPOJobName>
          </QueryProvider>
          <RecentPurchasesTileOrigin orderOrigin={order.orderOrigin} />
          <RecentPurchasesTileTotal orderTotal={order.totalAmount} />
        </div>
        <div className="sui-mt-3">
          <OrderItemsComponent
            isOnline={isOnline}
            customerInfo={customerInfo}
            layout="tile"
            order={order}
            OrderDetailsType={OrderDetailsType}
            orderDetailsUrl={orderDetailsUrl}
            index={index}
          />
        </div>
      </div>
    </QueryProvider>
  );
}

RecentPurchasesTile.propTypes = {
  order: shape({
    POJobName: string,
    detailsSource: string,
    orderNumber: string,
    orderNumbers: arrayOf(string),
    orderOrigin: string,
    receiptDetails: string,
    registerNumber: number,
    salesDate: string,
    totalAmount: number,
    transactionId: number,
    transactionType: string,
  }),
  customerInfo: shape({
    customerAccountId: string,
    userId: string,
  }),
  index: number,
  onError: func,
  /** Set true when rendering inside Dashboard Snapshot, to use its 4px corner radius. */
  isDashboardSnapshot: bool,
};

// Lets consumers mock this component's orderDetails/editPOJobName queries (e.g. via
// mockRequestResponse(RecentPurchasesTile, 'orderDetails', {...})) and makes it SSR-prefetch
// correctly when composed with extend() elsewhere.
RecentPurchasesTile.dataModel = extend(
  InstoreOrderDetails,
  OnlineOrderDetails,
  InstoreEditPOJobName,
  OnlineEditPOJobName,
);
